import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import collect, { Collection } from 'collect.js';
import useScreenType from '../../hooks/utility/useScreenType';
import useOrderingDisabledStatus from '../../hooks/availability/useOrderingDisabledStatus';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { Modifier, ModifierGroupInterface, ProductInterface } from '../../services/exports/Interfaces';
import ProductIdentifiers from '../../components-library/menu/product/ProductIdentifiers';
import useProductIdentifiers from '../../hooks/menu/useProductIdentifiers';
import ButtonFooter from '../../components-library/footers/ButtonFooter';
import CustomButton from '../../components-library/buttons/CustomButton';
import CustomNavBar from '../../components-library/navigation/CustomNavBar';
import SeoResource from '../../services/resources/SeoResource';
import ModifierGroup from '../../components-library/menu/ModifierGroup';
import useMenuItemModifiers from './useMenuItemModifiers';
import BasketHelpers from '../../services/helpers/BasketHelpers';
import { BasketLineItemModifier } from '../../store/types';
import { CompiledModifier, CompiledProduct } from '../../providers/MenuProvider';

type MenuItemModalProps = {
  data: CompiledProduct | CompiledModifier;
  selectedLineItem: BasketLineItemModifier;
  updateParentLineItem: (lineItem: BasketLineItemModifier) => void;
  scrollY: number;
  goBack: () => void;
};

const MenuItemModal = (props: MenuItemModalProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:MenuItemModal' });
  const { data, selectedLineItem, scrollY, goBack, updateParentLineItem } = props;
  const [lineItem, setLineItem] = useState<BasketLineItemModifier>(() => structuredClone(selectedLineItem));
  const { subItem, openSubItem, closeSubItem, getSubLineItem } = useMenuItemModifiers({
    scrollToModifierGroup,
  });
  const toggleModifier = (modifierGroup: ModifierGroupInterface, entry: ProductInterface | Modifier): void => {
    const _lineItem = BasketHelpers.toggleModifier({
      lineItem,
      entry,
      modifierGroup,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const extraPrice = data.actual_price;
  const { isDesktop } = useScreenType();
  const { render: renderOrderingDisabled } = useOrderingDisabledStatus();
  const { formatCurrency } = useAppMethods();

  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, ['image_url' in data && data?.image_url]);

  const scrollYRef = useRef<number>(scrollY);
  scrollYRef.current = scrollY;

  const [modifierGroupRefs] = useState<Collection<React.RefObject<HTMLDivElement>>>(() =>
    collect(data?.modifier_groups ?? [])
      .keyBy('id')
      .map(() => React.createRef()),
  );

  const [showValidation, setShowValidation] = useState<boolean>(false);

  const invalidModifierGroupIds = useMemo<Set<number>>(() => {
    if (!showValidation) {
      return new Set();
    }

    return new Set(
      collect(data?.modifier_groups ?? [])
        .filter((group: ModifierGroupInterface) => BasketHelpers.isModifierGroupRequired(group))
        .filter((group: ModifierGroupInterface) =>
          collect(lineItem.modifiers).where('modifier_group_id', group.id).isEmpty(),
        )
        .map((group: ModifierGroupInterface) => group.id)
        .all() as number[],
    );
  }, [showValidation, data?.modifier_groups, lineItem.modifiers]);

  useEffect(() => {
    bodyRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  function scrollToModifierGroup(id: number): void {
    const ref = modifierGroupRefs.get(id);

    ref?.current && ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  const onAddEntry = (): void => {
    const valid = BasketHelpers.validateModifiers(data, lineItem.modifiers);

    if (!valid) {
      const invalidModifierGroup = BasketHelpers.findInvalidModifierGroup(data, lineItem.modifiers);

      setShowValidation(true);
      invalidModifierGroup && scrollToModifierGroup(invalidModifierGroup.id);

      return;
    }

    updateParentLineItem(lineItem);
    goBack();
  };

  const isProduct = 'image_url' in data;
  const imageUrl = isProduct ? data?.image_url : '';
  const { decoratedName } = useProductIdentifiers(data as ProductInterface);

  const renderProductInfo = (): React.ReactElement => {
    return (
      <div className="mb-md px-md">
        <div className="flex justify-between">
          <h4 className="text-brand-text-primary break-words mb-sm mr-md shrink">{decoratedName}</h4>
          <div className="flex-none">
            {extraPrice > 0 && (
              <p className="text-muted-foreground line-clamp-1 text-right">+ {formatCurrency(extraPrice)}</p>
            )}
          </div>
        </div>
        <div>
          {isProduct && <ProductIdentifiers product={data} className="mb-sm" />}
          {isProduct && <p className="text-muted-foreground break-words text-sm">{data.description}</p>}
        </div>
      </div>
    );
  };

  const renderAddButton = (): React.ReactElement => {
    if (renderOrderingDisabled) {
      return renderOrderingDisabled;
    }

    if (!data?.is_available) {
      return (
        <div className={productUnavailableContainer}>
          <h4 className={productUnavailableTextStyle}>{t('error_messages.product_unavailable')}</h4>
        </div>
      );
    }

    return (
      <ButtonFooter className={addButtonContainer}>
        {/*
          The NESTED sub-item modal's save button — reached only when a modifier itself has modifier
          groups. Deliberately a different id from `menu-item-add-button` (the product modal's), since
          confusing the two silently tests the wrong screen.
        */}
        <CustomButton testId="menu-sub-item-save-button" title={t('buttons.save')} onClick={onAddEntry} />
      </ButtonFooter>
    );
  };

  const renderHeaderBar = useMemo(() => {
    const shouldFadeInHeader = scrollYRef.current > (headerRef.current?.clientHeight ?? 0);

    return (
      <div className={headerContainer} ref={headerRef}>
        {!!imageUrl && <div className={headerBackground(shouldFadeInHeader)} />}

        <CustomNavBar leftClick={goBack} hideCloseIcon transparent={!!imageUrl} />
      </div>
    );
  }, [isDesktop, imageUrl, scrollYRef.current, headerRef.current]);

  const renderProductPicture = useMemo((): React.ReactElement | null => {
    return isProduct && imageUrl ? (
      <div
        ref={bodyRef}
        className={productImageContainer}
        style={{
          marginTop: headerHeight * -1.5,
        }}
      >
        <img src={data?.thumbnail_url} alt={SeoResource.toAltText(`${data?.name} image`)} className={productImage} />
        <img src={imageUrl} alt={SeoResource.toAltText(`${data?.name} image`)} className={productImage} />
      </div>
    ) : (
      <div ref={bodyRef} />
    );
  }, [imageUrl, headerHeight, bodyRef]);

  const updateSubLineItem = (entryLineItem: BasketLineItemModifier): void => {
    const _lineItem = BasketHelpers.toggleModifier({
      lineItem,
      entryLineItem,
      modifierGroup: subItem.modifierGroup,
      entry: subItem.item,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const adjustQuantity = (
    modifierGroup: ModifierGroupInterface,
    entry: Modifier | ProductInterface,
    step: number,
  ): void => {
    const _lineItem = BasketHelpers.adjustQuantity({
      lineItem,
      entry,
      modifierGroup,
      step,
    });

    _lineItem && setLineItem(_lineItem);
  };

  const getSelectedModifiers = (modifierGroup: ModifierGroupInterface): BasketLineItemModifier[] =>
    lineItem.modifiers?.filter((item) => item.modifier_group_id === modifierGroup.id);

  return (
    <>
      {subItem ? (
        <MenuItemModal
          data={subItem.item}
          selectedLineItem={getSubLineItem(lineItem)}
          scrollY={scrollY}
          goBack={closeSubItem}
          updateParentLineItem={updateSubLineItem}
        />
      ) : (
        <div className="flex flex-col h-full">
          {renderHeaderBar}
          {renderProductPicture}
          <div className={wrapper(!!imageUrl)}>
            {renderProductInfo()}
            <div className="mb-md bg-brand-card pb-[100px]">
              {data?.modifier_groups?.map((modifierGroup: ModifierGroupInterface) => (
                <ModifierGroup
                  data={modifierGroup as ModifierGroupInterface<CompiledModifier>}
                  selectedModifiers={getSelectedModifiers(modifierGroup)}
                  toggle={(entry: CompiledProduct | CompiledModifier) => toggleModifier(modifierGroup, entry)}
                  ref={modifierGroupRefs.get(modifierGroup.id)}
                  key={`modifier-group-${modifierGroup.id}`}
                  openSubItem={(entry: CompiledProduct) => openSubItem(entry, modifierGroup)}
                  adjustQuantity={(entry, step) => adjustQuantity(modifierGroup, entry, step)}
                  hasError={invalidModifierGroupIds.has(modifierGroup.id)}
                />
              ))}
            </div>
          </div>
          {renderAddButton()}
        </div>
      )}
    </>
  );
};

export default MenuItemModal;

const headerContainer = 'sticky top-0 z-30 relative bg-brand-card';

const headerBackground = (fadeIn: boolean) =>
  classnames('bg-brand-card transition-opacity duration-500 ease-out border-b absolute inset-0', {
    'opacity-100': fadeIn,
    'opacity-0': !fadeIn,
  });

const productImageContainer = 'relative w-full aspect-square';

const productImage = 'object-cover absolute w-full h-full';

const wrapper = (hasOffset: boolean) =>
  classnames('pt-md h-full lg:pb-md pb-[100px] bg-brand-card relative flex-1', {
    'lg:-mt-[150px] -mt-[40px] border-t': hasOffset,
  });

const addButtonContainer = `lg:sticky fixed bottom-0 items-center`;

const productUnavailableContainer =
  'lg:sticky fixed bottom-0 w-full flex flex-row items-center justify-center px-lg py-md bg-gray border-t';

const productUnavailableTextStyle = 'text-muted-foreground text-center';
